// import React from "react";

// const IncentiveSheet = () => {
//   return <div>IncentiveSheet</div>;
// };

// export default IncentiveSheet;

import React, { useState, useEffect } from "react";
import {
  TableAdv,
  TagAdv,
  Button,
  SmartTooltip,
  showToast,
  CustomMonthPicker
} from "../../../../../../components";
import { useIncentiveRoles } from "../IncentiveModuleLayout";
import { dateFormator, mapTableRows } from "../../../../../../utils";
import { fetchIncentiveSheet, updateIncentive } from "../../../../../../redux/slices/incentives/incentivesApi";
import dayjs from "dayjs";

const actionColorMap = {
  Paid: "success",
  Unpaid: "danger",
  Pending: "warning",
  Eligible: "indigo",
  Approved: "success",
  Reject: "gray",
  Void: "gray"
};

const IncentiveSheet = () => {
  const { availableRoles, moduleName } = useIncentiveRoles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // IncentiveSheet shows ONLY eligible/approved/paid records
        const params = { 
          module: "IT", 
          status: "Eligible,Approved,Paid,Unpaid,Reject"
        };
        if (selectedMonth) {
          params.month = dayjs(selectedMonth).month() + 1;
          params.year = dayjs(selectedMonth).year();
        }
        const res = await fetchIncentiveSheet(params);
        // Map _id and related fields to strings for TableAdv compatibility
        const formattedRows = (res || []).map(row => {
            const pid = row._id?.placementGrp;
            const igrp = row._id?.invoiceGrp || "initial";
            const stringId = typeof row._id === 'object' ? `${pid}_${igrp}` : row._id;
            return {
                ...row,
                id: stringId,
                placementId: row.placementId?.toString() || row.placementId
            };
        });
        
        // Group conceptually: Sort by Consultant Name, then Initial vs Recurrent
        formattedRows.sort((a, b) => {
           if (a.placementId === b.placementId) {
               const aRecurrent = a._id?.invoiceGrp && a._id.invoiceGrp !== "initial";
               const bRecurrent = b._id?.invoiceGrp && b._id.invoiceGrp !== "initial";
               return (aRecurrent === bRecurrent) ? 0 : aRecurrent ? 1 : -1;  
           }
           const nameA = a.consultantName || '';
           const nameB = b.consultantName || '';
           return nameA.localeCompare(nameB);
        });

        setRows(formattedRows);
      } catch (error) {
        console.error("Failed to load incentive sheet data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [selectedMonth]);

  const handleRowToggle = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleStatusChange = async (newStatus, row) => {
    try {
      setLoading(true);
      // Update each team member's incentive record in parallel
      await Promise.all(
        row.teamMembers.map(member => 
          updateIncentive({
            coll: "PlacementIncentive",
            uiid: member.incentiveId,
            payload: [{ type: "SET", value: { status: newStatus } }]
          })
        )
      );
      
      // Optimitically update local state to reflect change
      setRows(prevRows => prevRows.map(r => {
        if (r.id === row.id) {
            return {
                ...r,
                teamMembers: r.teamMembers.map(m => ({ ...m, status: newStatus }))
            };
        }
        return r;
      }));
      
      showToast(`Status successfully updated to ${newStatus}`, "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to update status", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: "consultantName",
      label: "Consultant Name",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.teamMembers && row.teamMembers.length > 0 && (
            <Button
              icon={
                expandedRows[row.id]
                  ? "keyboard_arrow_down"
                  : "keyboard_arrow_right"
              }
              type="icon"
              variant="text"
              size="xs"
              color="black"
              onClick={(e) => {
                e.stopPropagation();
                handleRowToggle(row.id);
              }}
            />
          )}
          <SmartTooltip
            description={row?.consultantName || "N/A"}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1 font-medium">{row?.consultantName || "N/A"}</div>
          </SmartTooltip>
        </div>
      ),
    },
    {
      key: "jobTitle",
      label: "Job Title",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.jobTitle || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.jobTitle || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "isRecurrent",
      label: "Type",
      width: "w-[120px]",
      render: (row) => {
        const isRecurrent = row._id?.invoiceGrp && row._id.invoiceGrp !== "initial";
        return (
          <TagAdv
             title={isRecurrent ? "Recurrent" : "Initial"}
             size="sm"
             color={isRecurrent ? "indigo" : "gray"}
             colorDepth={600}
             className="w-[80px]! text-center"
          />
        );
      },
    },
    {
      key: "clientName",
      label: "Client",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.clientName || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.clientName || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "salary",
      label: "Salary",
      render: (row) => `$${row.salary || 0}`,
    },
    {
      key: "totalCommissionPercentage",
      label: "Percentage",
      render: (row) => `${row.totalCommissionPercentage || 0}%`,
    },
    {
      key: "totalCommissionAmount",
      label: "Commission",
      render: (row) => `$${row.totalCommissionAmount || 0}`,
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (row) => dateFormator(row.startDate),
    },
    {
      key: "endDate",
      label: "End Date",
      render: (row) => dateFormator(row.endDate),
    },
    {
      key: "unlockDate",
      label: "Unlock Date",
      render: (row) => row.unlockDate ? dateFormator(row.unlockDate) : "N/A",
    },
    {
      key: "status",
      label: "Status",
      width: "w-[130px]",
      render: (row) => {
        const statuses = [...new Set((row.teamMembers || []).map(m => m.status))];
        const displayStatus = statuses.length === 1 ? statuses[0] : (statuses.includes("Pending") ? "Pending" : "Mixed");
        
        if (displayStatus === "Eligible") {
            return (
              <div className="flex items-center">
                 <select 
                   className="text-[12px] font-medium leading-none p-1.5 rounded w-[100px] border border-indigo-200 bg-indigo-50 text-indigo-700 outline-none cursor-pointer"
                   value="Eligible"
                   onChange={(e) => {
                       if(e.target.value !== "Eligible") {
                           handleStatusChange(e.target.value, row);
                       }
                   }}
                 >
                    <option value="Eligible" disabled hidden>Eligible</option>
                    <option value="Paid" className="text-success-600 bg-success-50 font-medium">Paid</option>
                    <option value="Unpaid" className="text-danger-600 bg-danger-50 font-medium">Unpaid</option>
                    <option value="Reject" className="text-gray-600 bg-gray-50 font-medium">Reject</option>
                 </select>
              </div>
            );
        }

        return (
          <div className="flex items-center">
            <TagAdv
              title={displayStatus}
              size="sm"
              color={actionColorMap[displayStatus] || "gray"}
              colorDepth={600}
              className="w-[100px]! text-center"
            />
          </div>
        );
      },
    },
  ];

  const mappedRows = mapTableRows(rows, columns);

  const getResolvedRoleName = (item) => {
    // IT module allows [1, 2, 3]
    const allowedModules = [1, 2, 3];
    const roleId = item.roleId || item.roleType;
    if (!roleId || !availableRoles?.length) return item.roleName || "Unknown";
    
    // First try to resolve by UIID or Type
    const role = availableRoles.find(r => String(r.uiid) === String(roleId) || String(r.type) === String(roleId));
    
    if (role && allowedModules.includes(role.module)) {
      return role.name;
    }
    return "Unknown";
  };

  const AccordionComponent = ({ row }) => {
    if (!row.teamMembers || row.teamMembers.length === 0) return null;
    return (
      <div className="bg-[#FFF5EC] p-4 flex flex-col gap-2">
        <div className="flex w-full items-start gap-8 flex-wrap">
          <div className="flex flex-col gap-1 min-w-[max-content]">
            <span className="text-xs font-semibold text-gray-500">
              Invoice Number
            </span>
            {row.invoiceNum ? (
                <span 
                    className="text-sm font-medium text-gray-900"
                >
                  {row.invoiceNum}
                </span>
            ) : (
                <span className="text-sm font-medium text-gray-900">-</span>
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-[max-content] border-l border-orange-200 pl-4">
            <span className="text-xs font-semibold text-gray-500 pt-1">
              USD Total
            </span>
          </div>

          {row.teamMembers.map((item, idx) => {
            const resolvedRoleName = getResolvedRoleName(item);
            return (
              <div key={idx} className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                  {resolvedRoleName}{" "}
                  <span className="text-gray-500 font-normal">({item.name}) {item.rateUsed}%</span>
                </span>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {item.amount !== null ? `$${item.amount}` : ""}
                <span className="text-[10px] text-gray-400 font-normal">
                   (${row.totalCommissionAmount || 0} × {item.rateUsed || 0}%)
                </span>
              </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex-1 w-full flex flex-col overflow-hidden">
      <TableAdv
        tableId={"incentive_incentivemodule_it_incentiveheet"}
        columns={columns}
        rows={mappedRows}
        toolbarRight={{ 
          search: true,
          add: [
            {
              component: (
                <CustomMonthPicker
                  selected={selectedMonth}
                  onChange={(date) => setSelectedMonth(date)}
                  size="sm"
                />
              )
            }
          ]
        }}
        accordion={<AccordionComponent />}
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        isLoading={loading}
        fixedCol={1}
      />
    </div>
  );
};

export default IncentiveSheet;
